import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currencyGlyph } from '@/lib/money/index.js';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { useMoney } from '../../../lib/money/useMoney.js';
import { minorToPlain } from '../state/bet.js';
import type { OriginalsConfig } from '../types.js';
import { itemUrl } from '../scenes/scratch/scratchArt.js';

const BP = 10000n;

/**
 * The prize's picture: the same rendered item the scene draws under the foil (a cherry is
 * the card's gold star, a gem its ruby), so what the table promises is what the player
 * scratches to. When the art cannot load, the scene falls back to its drawn shapes, and
 * so does the table.
 */
function PrizeArt({ symbol }: { symbol: string }) {
  const [failed, setFailed] = useState(false);
  const src = itemUrl(symbol, 1);
  if (!src || failed) return <SymbolArt symbol={symbol} />;
  return (
    <img
      src={src}
      srcSet={`${src} 1x, ${itemUrl(symbol, 2)} 2x`}
      alt=""
      aria-hidden
      draggable={false}
      className="h-9 w-9 object-contain drop-shadow-[0_2px_3px_rgba(0,0,0,0.35)]"
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Filled mini replicas of ScratchScene's Pixi-drawn tile art (same shapes, same
 * SYMBOL_COLOR palette), for when the rendered items are not there.
 */
function SymbolArt({ symbol, size = 22 }: { symbol: string; size?: number }) {
  switch (symbol) {
    case 'cherry':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
          <path d="M13.6 3.2 8.2 13.2M13.6 3.2 16.8 12" stroke="#2f7d32" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <circle cx="8" cy="15.6" r="4.3" fill="#ef4444" />
          <circle cx="16.2" cy="14.6" r="4.3" fill="#ef4444" />
          <circle cx="6.7" cy="14.2" r="1.3" fill="#ffffff" opacity="0.4" />
          <circle cx="15" cy="13.2" r="1.3" fill="#ffffff" opacity="0.4" />
        </svg>
      );
    case 'bell':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="4.6" r="1.3" fill="#c47b0b" />
          <path d="M6.3 15.2 7.2 10.4 a4.9 4.9 0 0 1 9.6 0 L17.7 15.2 Z" fill="#f59e0b" />
          <rect x="5.2" y="15.2" width="13.6" height="1.8" rx="0.9" fill="#d3870c" />
          <circle cx="12" cy="18.6" r="1.5" fill="#b9770a" />
        </svg>
      );
    case 'coin':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="8.6" fill="#facc15" />
          <circle cx="12" cy="12" r="8.6" fill="none" stroke="#b8960f" strokeWidth="1.7" />
          <circle cx="12" cy="12" r="5.5" fill="none" stroke="#fde68a" strokeWidth="1.3" opacity="0.9" />
          <circle cx="9.2" cy="9" r="1.9" fill="#ffffff" opacity="0.35" />
        </svg>
      );
    case 'gem':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 2.8 17 9 12 21.2 7 9 Z" fill="#22d3ee" />
          <path d="M7 9 H17" stroke="#7eebfa" strokeWidth="1.2" opacity="0.85" fill="none" />
          <path d="M12 2.8 V21.2" stroke="#16919f" strokeWidth="1" opacity="0.5" fill="none" />
        </svg>
      );
    case 'lemon':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
          <g transform="rotate(-18 12 12)">
            <ellipse cx="12" cy="12" rx="8.2" ry="5.6" fill="#cddc39" />
            <circle cx="3.8" cy="12" r="1.1" fill="#a9b832" />
            <circle cx="20.2" cy="12" r="1.1" fill="#a9b832" />
            <ellipse cx="9.5" cy="10.2" rx="2.4" ry="1.1" fill="#ffffff" opacity="0.3" />
          </g>
        </svg>
      );
    case 'star':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
          <path d="M12 2.5 14.9 9 21.8 9.7 16.7 14.4 18.1 21.3 12 17.8 5.9 21.3 7.3 14.4 2.2 9.7 9.1 9 Z" fill="#f5c518" />
        </svg>
      );
    case 'seven':
      return (
        <span
          className="text-[17px] font-black italic leading-none"
          style={{ color: '#a855f7' }}
          aria-hidden
        >
          7
        </span>
      );
    default:
      return (
        <span className="text-[12px] font-black uppercase text-[var(--text-secondary)]" aria-hidden>
          {symbol.slice(0, 2)}
        </span>
      );
  }
}

/**
 * Literal paytable for the scratch card: each paying tier of the CURRENT risk
 * with its prize symbol, multiplier, and the payout on the current bet — the
 * scratch counterpart of MinesPayoutPreview's "what if" chips. The top tier is
 * highlighted as the jackpot.
 */
export function ScratchLegend({ config, bet }: { config: OriginalsConfig; bet: bigint }) {
  const { t } = useTranslation('originals');
  const { multiplier } = useMoney();
  const legendCurrency = useMoneyCurrency();
  const mult = multiplier();

  const prizeMap =
    (config.presenterConfig as { prizeMap?: Record<string, string> } | null | undefined)
      ?.prizeMap ?? {};
  const tiers = config.outcomes
    .filter((o) => o.multiplierBp > 0)
    .sort((a, b) => a.multiplierBp - b.multiplierBp);
  if (tiers.length === 0) return null;

  const topBp = tiers[tiers.length - 1]!.multiplierBp;

  return (
    <div className="mt-4 flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <span className="text-[12px] font-bold text-[var(--text-primary)]">
          {t('scratch.legend.title')}
        </span>
        <span className="text-[11.5px] leading-relaxed text-[var(--text-muted)]">
          {t('scratch.legend.hint')}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tiers.map((tier) => {
          const symbol = prizeMap[String(tier.multiplierBp)];
          const m = tier.multiplierBp / 10000;
          const payout = (bet * BigInt(tier.multiplierBp)) / BP;
          const jackpot = tier.multiplierBp === topBp && tiers.length > 1;
          return (
            <div
              key={tier.multiplierBp}
              className={[
                'flex min-w-[64px] flex-1 flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5',
                jackpot
                  ? 'bg-[var(--primary)]/15 border border-[var(--primary)]/50'
                  : 'bg-[var(--bg-elevated)] border border-[var(--border)]',
              ].join(' ')}
            >
              <span className="grid h-9 place-items-center">
                {symbol ? <PrizeArt symbol={symbol} /> : null}
              </span>
              <span
                className={[
                  'text-[13px] font-extrabold tabular-nums',
                  jackpot ? 'text-[var(--warning)]' : 'text-[var(--primary)]',
                ].join(' ')}
              >
                ×{m % 1 === 0 ? m.toFixed(0) : m.toFixed(2)}
              </span>
              <span className="text-[10px] tabular-nums text-[var(--text-muted)]">
                {currencyGlyph(legendCurrency)}
                {minorToPlain(payout, mult)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
